import { UiDictionaryKey } from "@plansoft-configuration-renderer/localization";

import { isHttpError } from "./httpError";

interface AppErrorBody {
  key?: string;
  message?: string;
  params?: Record<string, unknown>;
}

export type ClassifiedQueryError =
  | { kind: "maintenance" }
  | {
      kind: "toast";
      key?: UiDictionaryKey;
      params?: Record<string, unknown>;
      fallbackMessage?: string;
    }
  | { kind: "ignore" };

/**
 * Decides what our generic error-handling system should do with a
 * react-query error, on top of whatever CpqCore's own built-in
 * queryCache/mutationCache `onError` already does (that keeps running
 * unchanged — see `QueryErrorBridge`, which observes the cache additively
 * instead of replacing that handler).
 */
export function classifyQueryError(error: unknown): ClassifiedQueryError {
  if (!isHttpError(error)) {
    // Not one of our own orval-generated calls (e.g. a network failure, or a
    // CpqCore-internal hey-api error) — CpqCore's existing generic toast
    // already covers these, nothing more to do here.
    return { kind: "ignore" };
  }

  if (error.status === 503) {
    return { kind: "maintenance" };
  }

  const body = error.info as AppErrorBody | undefined;
  if (!body?.key && !body?.message) {
    // No app-specific message to show — let CpqCore's fallback toast handle it.
    return { kind: "ignore" };
  }

  return {
    kind: "toast",
    key: body.key ? (`CustomApp:${body.key}` as UiDictionaryKey) : undefined,
    params: body.params,
    fallbackMessage: body.message,
  };
}
